import { NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, computed, inject } from '@angular/core';
import { CopyRightComponent } from '../../shared/copy-right/copy-right.component';
import { FsthpMasterDataStore } from '../services/fsthp-master-data.store';
import { GlossaryTermRowComponent } from './components/glossary-term-row/glossary-term-row.component';
import { GlossaryTerm } from './models/glossary-term.model';
@Component({
  selector: 'app-glossary-modal',
  standalone: true,
  imports: [GlossaryTermRowComponent, NgOptimizedImage, CopyRightComponent],
  templateUrl: './glossary-modal.component.html',
  styleUrls: ['./glossary-modal.component.scss'],
})
export class GlossaryModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;
  @ViewChild('closeButton') private closeButtonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('scrollContent') private scrollContentRef?: ElementRef<HTMLElement>;

  private readonly masterDataStore = inject(FsthpMasterDataStore);

  /** 用語マスタ（WORD_NAME / EXPLANATION）を WORD_ID 数値昇順で全件表示する */
  protected readonly terms = computed<GlossaryTerm[]>(() =>
    this.masterDataStore.getWordExplanationList().map((word) => ({
      wordId: word.wordId,
      term: word.wordName ?? '',
      description: word.explanation ?? '',
    })),
  );
  protected thumbHeight = 0;
  protected thumbOffset = 0;
  protected showScrollbar = false;

  private previouslyFocusedElement: HTMLElement | null = null;
  private bodyOverflow = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) {
      return;
    }

    if (this.isOpen) {
      this.openModal();
      return;
    }

    this.closeModalSideEffects();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
    this.restoreFocus();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  onOverlayClick(): void {
    this.close();
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onCloseClick(): void {
    this.close();
  }

  onScroll(): void {
    this.updateScrollbar();
  }

  private openModal(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    this.lockBodyScroll();

    requestAnimationFrame(() => {
      this.closeButtonRef?.nativeElement.focus();
      this.updateScrollbar();
    });
  }

  private close(): void {
    this.closed.emit();
  }

  private closeModalSideEffects(): void {
    this.unlockBodyScroll();
    this.restoreFocus();
  }

  private lockBodyScroll(): void {
    this.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = this.bodyOverflow;
  }

  private restoreFocus(): void {
    if (this.previouslyFocusedElement?.focus) {
      this.previouslyFocusedElement.focus();
    }
    this.previouslyFocusedElement = null;
  }

  private trapFocus(event: KeyboardEvent): void {
    const panel = this.panelRef?.nativeElement;
    if (!panel) {
      return;
    }

    const focusable = this.getFocusableElements(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);
  }

  private updateScrollbar(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) {
      return;
    }

    const { scrollHeight, clientHeight, scrollTop } = content;
    const canScroll = scrollHeight > clientHeight;
    this.showScrollbar = canScroll;

    if (!canScroll) {
      this.thumbHeight = 0;
      this.thumbOffset = 0;
      return;
    }

    const trackHeight = clientHeight;
    const ratio = clientHeight / scrollHeight;
    this.thumbHeight = Math.max(trackHeight * ratio, 24);
    const maxThumbOffset = trackHeight - this.thumbHeight;
    const maxScrollTop = scrollHeight - clientHeight;
    this.thumbOffset = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbOffset : 0;
  }
}
